import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { BehaviorSubject, Observable, Subject, debounceTime } from 'rxjs';
import { Category, Files } from 'src/app/models';
import { UploadSources, UploadSourcesNamming, ViewModes } from '../models';
import { AuthService } from 'src/app/shared/services/auth.service';
import { environment } from 'src/environments/environment';
import { NzMessageService } from 'ng-zorro-antd/message';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FileHttpService } from 'src/app/shared/services/http/file-http.service';
import { AttachmentsModalComponent } from 'src/app/shared/modals/attachments-modal/attachments-modal.component';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { UploadFilesModalComponent } from 'src/app/shared/modals/upload-files/upload-files.component';
import { maxFileSizeInBytes } from 'src/app/app.constants';
import { FilenamePipe } from 'src/app/shared/pipes/filename.pipe';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class FileManagerService {
  readonly _http = inject(HttpClient);
  readonly authService = inject(AuthService);
  readonly clipboard = inject(Clipboard);
  readonly message = inject(NzMessageService);
  readonly fileHttp = inject(FileHttpService);
  readonly modal = inject(NzModalService);
  readonly filenamePipe = inject(FilenamePipe);
  triggerResetFilters$ = new Subject();
  afterUploadCompleted$ = new Subject();
  view$ = new BehaviorSubject<ViewModes>(
    (localStorage.getItem('view') || 'medium') as ViewModes
  );
  limit$ = new BehaviorSubject<number>(
    Number(localStorage.getItem('limit')) || 50
  );
  page$ = new BehaviorSubject<number>(1);
  sort$ = new BehaviorSubject<string>(localStorage.getItem('sort') || 'newest');
  loading$ = new BehaviorSubject<boolean>(false);
  files$ = new BehaviorSubject<Files>({
    files: [],
    filesCount: 0,
  });
  recents$ = new BehaviorSubject<Files[]>([]);
  loadingMoreRecentFiles$ = new BehaviorSubject<boolean>(false);
  filterApplied$ = new BehaviorSubject<boolean>(false);
  selectedFiles = [];
  selectedFiles$ = new BehaviorSubject<Files[]>([]);
  categories$ = new BehaviorSubject<Category[]>([]);
  tags$ = new BehaviorSubject<any>([]);
  sources$ = new BehaviorSubject<any>([
    {
      label: UploadSourcesNamming.cart,
      value: [UploadSources.cart, UploadSources.cartItem],
    },
    {
      label: UploadSourcesNamming.workflow,
      value: [UploadSources.workflow, UploadSources.workflowItem],
    },
    {
      label: UploadSourcesNamming.account,
      value: [UploadSources.account, UploadSources.accountItem],
    },
    {
      label: UploadSourcesNamming.workshop,
      value: [UploadSources.workshop, UploadSources.workshopItem],
    },
    {
      label: UploadSourcesNamming.fabric,
      value: [UploadSources.fabric],
    },
    {
      label: UploadSourcesNamming.item,
      value: [UploadSources.item],
    },
    {
      label: UploadSourcesNamming.component,
      value: [UploadSources.component],
    },
    {
      label: UploadSourcesNamming.survey,
      value: [
        UploadSources.survey,
        UploadSources.surveyLocation,
        UploadSources.surveySublocation,
        UploadSources.surveyAccess,
      ],
    },
    {
      label: UploadSourcesNamming.calendar,
      value: [UploadSources.calendar],
    },
    {
      label: UploadSourcesNamming.other,
      value: [UploadSources.other],
    },
  ]);

  constructor() {
    if (this.authService.currentUserValue) {
      if (!this.categories$.getValue().length) {
        this.getCategories();
      }

      if (!this.tags$.getValue().length) {
        this.getTags();
      }

      this.afterUploadCompleted$
        .pipe(debounceTime(1000), untilDestroyed(this))
        .subscribe(() => {
          this.getTags();
        });
    }
  }

  linksCopied = false;
  public copyLinksClipboard(listOfSelectedFiles) {
    this.linksCopied = true;
    let results = listOfSelectedFiles
      .reduce((acc, file) => {
        acc.push(`${environment.appUrl}/file/${file.uuid.toString()}`);
        return acc;
      }, [])
      .toString()
      .replaceAll(',', '\r\n');
    this.clipboard.copy(results);
    this.clipboardNotification();
  }

  galleryLinkCopied = false;
  public generateGallery(listOfSelectedFiles) {
    this.galleryLinkCopied = true;
    this.fileHttp
      .createFilesGallery({
        sendToExtCarousel: listOfSelectedFiles.map(i => i.id),
      })
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        const address = `${environment.appUrl}/gallery/${res
          .toString()
          .split('/')
          .pop()}`;
        this.clipboard.copy(address);
        window.open(address, '_blank');
        this.clipboardNotification();
      });
  }

  private clipboardNotification() {
    this.message.create('success', 'Copied to clipboard');
    setTimeout(() => {
      this.linksCopied = false;
      this.galleryLinkCopied = false;
    }, 2000);
  }

  loadMoreRecents(): void {
    this.loadingMoreRecentFiles$.next(true);
    const recents = this.recents$.getValue();
    this.fileHttp
      .getRecentFiles(recents.length)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.recents$.next([...recents, ...res]);
          this.loadingMoreRecentFiles$.next(false);
        },
      });
  }

  getCategories(): void {
    this.fileHttp
      .getCategories()
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this.categories$.next(res);
      });
  }

  toggleSelection(file) {
    const index = this.selectedFiles.indexOf(file);

    if (index == -1) {
      this.selectedFiles.push(file);
    } else {
      this.selectedFiles.splice(index, 1);
    }

    this.selectedFiles$.next(this.selectedFiles);
  }

  isFileSelected(id): boolean {
    return this.selectedFiles.find(i => i.id === id);
  }

  selectAll(files) {
    this.selectedFiles = [...files];
    this.selectedFiles$.next(this.selectedFiles);
  }

  deselectAll() {
    this.selectedFiles = [];
    this.selectedFiles$.next([]);
  }

  reset() {
    this.filterApplied$.next(false);
    this.page$.next(1);
    this.files$.next({
      files: [],
      filesCount: 0,
    });
    this.selectedFiles = [];
    this.selectedFiles$.next([]);
  }

  getTags(): void {
    this.fileHttp
      .getTags()
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        const tags = [];
        for (const o of res) {
          tags.push({ label: o.tag, value: o.tag });
        }
        this.tags$.next(tags);
      });
  }

  uploadToNFS(formData, fileData): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http
        .post('/files', formData)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            if (res[0].error) {
              reject(res[0]);
            } else {
              this.fileHttp
                .createFileDetails({
                  filename: res[0].filename,
                  filetype: res[0].filetype,
                  thumbnail: res[0].thumbnail,
                  url: res[0].uploadedFilename,
                  category: fileData.category || '',
                  tag: fileData.tag?.toString() || '',
                  extension: fileData.extension,
                  source_name: fileData.source_name,
                  source_id: fileData.source_id || 0,
                  location: fileData.location,
                  size: fileData.size,
                  platform: 'web',
                })
                .pipe(untilDestroyed(this))
                .subscribe({
                  next: res => {
                    this.addTags(fileData.tag);
                    this.afterUploadCompleted$.next(true);
                    resolve(res);
                  },
                  error: err => reject(err),
                });
            }
          },
          error: err => {
            reject(err);
          },
        });
    });
  }

  addTags(tags: any[]): void {
    if (tags?.length) {
      const existing = this.tags$.getValue();
      tags.forEach(tag => {
        if (!existing.find(i => i.value === tag)) {
          this.fileHttp.createTag({ tag }).toPromise();
        }
      });
    }
  }

  updateFile(file, id): Observable<any> {
    return this.fileHttp.updateFileDetails(id, file);
  }

  deleteFile(id) {
    return this._http.delete('/file_detail/' + id);
  }

  getRecents() {
    this.loading$.next(true);
    this.fileHttp
      .getRecentFiles()
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this.recents$.next(res);
        this.loading$.next(false);
      });
  }

  processDroppedFiles(data = { files: [], limit: Infinity }, callback) {
    if (data.files.length > 0) {
      const array = Array.from(data.files);
      const fileSizeExceed = array.filter(
        (file: any) => file.size > maxFileSizeInBytes
      );

      const filteredFiles = array
        .filter((file: any) => file.size <= maxFileSizeInBytes)
        .splice(0, data.limit);

      if (fileSizeExceed.length) {
        this.message.create(
          'error',
          'Some files were not added because are too large'
        );
      }

      filteredFiles.forEach(async (file: any, key) => {
        const base64 = await this.getBase64(file);
        file.filename = file.name;
        file.filetype = file.type.split('/')[0];
        file.thumbnail = file.filetype === 'image' ? base64 : undefined;
        file.url = base64;
        file.extension = file.name.split('.').pop();
        file.pending = true;

        if (filteredFiles.length === key + 1) {
          const result = this.fixDuplicatedFileNames(filteredFiles);
          callback(result as any[]);
        }
      });
    }
  }

  private nameDuplicationCount: { [key: string]: number } = {};
  private fixDuplicatedFileNames(files) {
    files.forEach(file => {
      file.category = this.defaultCategory();

      let { filename } = file;
      if (filename in this.nameDuplicationCount) {
        const count = this.nameDuplicationCount[filename];
        file.filename = this.generateUniqueName(file.filename, count);
        this.nameDuplicationCount[filename] = count + 1;
      } else {
        this.nameDuplicationCount[filename] = 1;
      }
    });

    return files;
  }

  private defaultCategory() {
    return this.categories$.getValue().find(i => i.default)?.value;
  }

  private generateUniqueName(originalName: string, count: number): string {
    const nameParts = originalName.split('.');
    if (nameParts.length > 1) {
      const ext = nameParts.pop();
      const baseName = nameParts.join('.');
      return `${baseName}-${count}.${ext}`;
    }
    return `${originalName}-${count}`;
  }

  private getBase64(file) {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = error => {
        resolve(undefined);
      };
    });
  }

  fileAsFormData(file): FormData {
    const formData = new FormData();
    const regex = /(?:\.([^.]+))?$/;
    const extension = regex.exec(file.name)[1];
    const filename = `${this.filenamePipe.transform(file.filename)}.${extension}`;
    formData.append('file', file, filename);
    return formData;
  }

  uploadPrompt(
    source_name = UploadSources.other,
    source_id = undefined,
    files = []
  ) {
    return this.modal.create({
      nzContent: UploadFilesModalComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'sm',
      nzCentered: true,
      nzData: {
        source_name,
        source_id,
        files,
      },
    });
  }

  openAttachments(
    data: {
      files: any[];
      index?: number;
      sourceName?: `${UploadSources}`;
      sourceId?: number;
      allowUpload?: boolean;
    } = {
      files: [],
      index: 0,
      sourceName: null,
      sourceId: null,
      allowUpload: false,
    }
  ): NzModalRef {
    let payload: any = {
      files: data.files ?? [],
      index: data.index ?? 0,
    };

    if (data.allowUpload) {
      if (!data.sourceName || !data.sourceId) {
        throw new Error(
          'Source name and source id is mandatory when allowing upload'
        );
      } else {
        payload.allowUpload = data.allowUpload;
        payload.sourceName = data.sourceName;
        payload.sourceId = data.sourceId;
      }
    }

    return this.modal.create({
      nzContent: AttachmentsModalComponent,
      nzClosable: false,
      nzFooter: null,
      nzMaskClosable: false,
      nzCentered: true,
      nzWidth: 'auto',
      nzStyle: {
        maxWidth: '100%',
        minWidth: '488px',
      },
      nzData: payload,
    });
  }
}
